import { AlignLayoutGroupCenter } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/LayoutGroupUtils';
import { CardObject } from '@/game/prefabs/StudentGame/CardDropSite.types';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import PlaceholderMinigameCard from '@/game/scenes/PlaceholderStudentGameCard';

export default class SequencedMinigameCardLayoutGroup {
  data: CardLayoutGroupData;
  isRealigning: boolean = false;
  maxCards: number = 12; // Arbitrary.
  // We need to allow for placeholder cards to be added to the layout group.
  cards: CardObject[] = [];
  rowCount: number = 1;
  rowHeight: number = 64;
  debugGraphics: any;

  constructor(data: CardLayoutGroupData) {
    this.data = data;
    this.updateLayout();
  }

  addCard(
    card: CardObject,
    index: number,
    realignmentOverride?: 'instant' | 'animated',
  ): boolean {
    const { parent } = this.data;
    this.cards.splice(index, 0, card);

    card.scene.tweens.killTweensOf(card);

    this.setCardPositionForAdd(card);

    parent.add(card);
    parent.moveTo(card, index);

    this.updateLayout(realignmentOverride);
    return true;
  }

  moveCard(card: CardObject, index: number) {
    const currentIndex = this.cards.indexOf(card);
    if (currentIndex === index) {
      return;
    }
    if (currentIndex === -1) {
      console.error('Card not found in layout group');
      return;
    }

    // Swap the cards around.
    const cardAtIndex = this.cards[index];
    this.cards[currentIndex] = cardAtIndex;
    this.data.parent.moveTo(cardAtIndex, currentIndex);
    this.cards[index] = card;
    this.data.parent.moveTo(card, index);
    this.updateLayout();
  }

  pushCard(card: CardObject) {
    this.data.parent.add(card);
    this.cards.push(card);
    this.updateLayout();
  }

  destroyCard(card: CardObject) {
    this.data.parent.remove(card);
    card.destroy();
    this.cards.splice(this.cards.indexOf(card), 1);
    this.updateLayout();
  }

  removeCard(card: CardObject) {
    const index = this.cards.indexOf(card);
    if (index === -1) {
      return;
    }
    const cardParentTransform = this.data.parent.getWorldTransformMatrix();
    card.setPosition(
      card.x + cardParentTransform.tx,
      card.y + cardParentTransform.ty,
    );

    this.data.parent.removeAt(index);
    this.cards.splice(index, 1);
    this.updateLayout();
  }

  getMinigameCards() {
    return this.data.parent.list.filter(
      (element) => element instanceof StudentGameCard,
    ) as StudentGameCard[];
  }

  getGlobalPositionFromIndex(index: number): Phaser.Math.Vector2 {
    const card = this.cards[index];
    if (card) {
      const cardPos = card.getWorldTransformMatrix();
      return new Phaser.Math.Vector2(cardPos.tx, cardPos.ty);
    }
    return new Phaser.Math.Vector2(0, 0);
  }

  updateLayout(_realignmentTypeOverride?: 'instant' | 'animated') {
    AlignLayoutGroupCenter(this);
  }

  showPlaceholderCard() {}

  cancelAlignment() {
    this.isRealigning = false;
    this.data.parent.scene.tweens.killTweensOf(this.data.parent);
  }

  positionElement(
    element: Phaser.GameObjects.GameObject &
      Phaser.GameObjects.Components.Transform,
    x: number,
    y: number,
    realignmentTypeOverride?: 'instant' | 'animated',
  ) {
    if (element instanceof PlaceholderMinigameCard) {
      realignmentTypeOverride = 'instant';
    } else if (element instanceof StudentGameCard) {
      if (element.origin) {
        const worldTransform = this.data.parent.getWorldTransformMatrix();
        const localPos = worldTransform.transformPoint(x, y);
        element.origin = {
          ...element.origin,
          desiredPosition: { x: localPos.x, y: localPos.y },
        };
      }
    }
    const { data } = this;
    switch (realignmentTypeOverride || data.realignmentType) {
      case 'instant':
      default:
        element.setPosition(x, y);
        break;
      case 'animated':
        element.scene.tweens.killTweensOf(element);
        this.isRealigning = true;
        data.parent.scene.tweens.add({
          targets: element,
          x: x,
          y: y,
          duration: 300,
          ease: 'Cubic.easeOut',
          onComplete: () => {
            this.isRealigning = false;
          },
        });
    }
  }

  setCardPositionForAdd(card: CardObject) {
    const { parent } = this.data;
    if (!card.parentContainer && card instanceof StudentGameCard) {
      const parentTransform = parent.getWorldTransformMatrix();
      card.setPosition(
        card.x - parentTransform.tx,
        card.y - parentTransform.ty,
      );
    }
    if (
      card.parentContainer &&
      card instanceof StudentGameCard &&
      card.parentContainer !== parent
    ) {
      const parentTransform = parent.getLocalTransformMatrix();
      card.setPosition(
        card.x - parentTransform.tx,
        card.y - parentTransform.ty,
      );
    }
  }

  findCard(
    card: StudentGameCard | PlaceholderMinigameCard,
  ): StudentGameCard | PlaceholderMinigameCard | null {
    return this.cards.find((c) => c === card) as
      | StudentGameCard
      | PlaceholderMinigameCard
      | null;
  }

  setMaxCards(newMax: number) {
    this.data.maxCards = newMax;
    this.updateLayout();
  }

  reset() {
    for (var i = 0; i < this.cards.length; i++) {
      this.cards[i]?.destroy();
    }
    this.cards = [];
    this.updateLayout();
  }

  getCardCount() {
    return this.cards.filter((card) => card instanceof StudentGameCard).length;
  }
}

export interface CardLayoutGroupData {
  parent: Phaser.GameObjects.Container;
  elementDist: number;
  maxCards: number;
  origin: { x: number; y: number };
  realignmentType: 'instant' | 'animated';
  width?: number;
  maxWidth?: number;
}
